import { useDispatch } from 'react-redux';
import { useAppSelector } from '@hooks/reduxHooks';
import { baseErrorHandler } from '@queries/baseErrorHandler';
import { setCollectionAudienceItem, setTotalElements } from '@reducers/roboto-call-slice';
import { $api } from '@service/RequestService';
import { isEmptyArr, timeStampToDate } from '@utils/index';
import { Pagination } from '@utils/models/Pagination';
import { useQuery } from 'react-query';

import { SearchParamsRobotCalls } from '../components/roboto-drawer/RobotoDrawer';
import { queryValuesWithFields } from '../utils/constants/call-collection';
import { endPoints } from '../utils/constants/endPoints';
import { queryKeys } from '../utils/constants/queryKeys';
import { AutoQueryGenerationType, CollectionOneCallDataType, CollectionQueryValueType, HouseCompactType } from '../utils/models/call-collection';
import { ChannelModel } from '../utils/models/ChannelModel';
import { SipModel } from '../utils/models/SipModel';
import { SpeechKitFieldsType, TranslatedField } from '../utils/models/speech-kit-fields';

export function useGetRobotoSip() {
  return useQuery<Pagination<SipModel>>({
    queryKey: queryKeys.ROBOTO_SIP,
    queryFn: async () => {
      const res = await $api.get(`${endPoints.ROBOTO_SIP}?page=1&size=10`);

      return res.data;
    },
    onError: baseErrorHandler
  });
}

export function useGetAllCampaigns(searchParams: SearchParamsRobotCalls) {
  return useQuery<Pagination<CollectionOneCallDataType>>({
    queryKey: [queryKeys.CAMPAGING_ALL, { ...searchParams }],
    queryFn: async () => {
      const res = await $api.post(endPoints.CAMPAGING_ALL, {
        ...searchParams,
        // eslint-disable-next-line no-nested-ternary
        statuses:
          searchParams?.statuses &&
          (Array.isArray(searchParams?.statuses) ? searchParams?.statuses : [searchParams?.statuses]),
        page: searchParams?.page || 1,
        size: searchParams?.size || 15
      });

      const data: Pagination<CollectionOneCallDataType> = res?.data?.data

      return data ?
        {
          ...data,
          data: data?.data?.map(item => ({
            ...item,
            startDate: item?.startDate ?
              timeStampToDate({ date: item?.startDate, timeStamp: item?.startTimestamp }) :
              null,
            cancelledDate: item?.cancelledDate ?
              timeStampToDate({ date: item?.cancelledDate, timeStamp: item?.cancelledTimestamp }) :
              null,
            createdDate: timeStampToDate({ date: item?.createdDate, timeStamp: item?.createdTimestamp }),
            endDate: item?.endDate ?
              timeStampToDate({ date: item?.endDate, timeStamp: item?.endTimestamp }) :
              null
          }))
        } : res?.data?.data;
    },
    onError: baseErrorHandler
  });
}

export function useGetOneCampagin(id: number) {
  return useQuery<CollectionOneCallDataType>({
    queryKey: [queryKeys.ONE_CAMPAGING, id],
    queryFn: async () => {
      const res = await $api.get(`${endPoints.ONE_CAMPAGING}/${id}`);

      return res?.data?.data;
    },
    onError: baseErrorHandler
  });
}

export function useGetAllChannelsShort(props: { enabled?: boolean } = {}) {
  return useQuery<ChannelModel[]>({
    queryKey: [queryKeys.CHANNEL_SHORT],
    queryFn: async () => {
      const res = await $api.get(endPoints.CHANNEL_SHORT);

      return res?.data?.data;
    },
    enabled: props?.enabled,
    onError: baseErrorHandler
  });
}

export function useCollectionEmployees() {
  const dataType = useAppSelector(({ robotoCallReducer }) => robotoCallReducer.audience.dataType);

  const { data, ...arg } = useQuery<{ employees: { id: number; fullName: string }[] }>(
    ['collection-employee-select'],
    async () => {
      if (dataType === 'LEAD') {
        const { data } = await $api.get('/v1/lead/employee/all');

        return { employees: data?.data };
      }

      if (dataType === 'DEBT') {
        const { data } = await $api.get('/v1/contract/employee');

        return { employees: data?.data };
      }

      return { employees: [] };
    },
    {
      enabled: !!dataType
    }
  );

  return { ...data, ...arg };
}

export function useAutoQueryGeneration() {
  const dispatch = useDispatch();
  const { dataType, queries, id } = useAppSelector(({ robotoCallReducer }) => robotoCallReducer.audience);

  const payload: AutoQueryGenerationType = {
    id,
    dataType,
    filter: {
      queries: queries
        ?.filter(
          ({ check, field, date, value, ids, subIds }) =>
            check && field && (date || value || !isEmptyArr(ids) || !isEmptyArr(subIds))
        )
        ?.map(({ check, field, date, value, ids, subIds }) => ({
          check,
          field,
          ids: isEmptyArr(ids) ? null : ids,
          subIds,
          value,
          date
        }))
    }
  };

  const enabled = queries?.every(({ check, field, ...arg }) => {
    if (check && field) {
      let key: 'value' | 'ids' | 'subIds' | 'date' = 'value';
      let type: CollectionQueryValueType = 'input';

      const queryTypeKeys = {
        input: 'value',
        datepicker: 'date',
        multiSelect: 'ids'
      } as const;

      Object.keys(queryValuesWithFields).forEach(key => {
        if (queryValuesWithFields[key as keyof typeof queryValuesWithFields]?.some(value => value === field)) {
          type = key as keyof typeof queryValuesWithFields;
        }
      });

      key = queryTypeKeys[type];

      // @ts-expect-error
      return type === 'multiSelect' ? !isEmptyArr(arg?.ids || []) || !isEmptyArr(arg?.subIds || []) : arg?.[key];
    }

    return false;
  });

  const { data, ...arg } = useQuery<{ query: AutoQueryGenerationType }>(
    ['collection-auto-query-generation', JSON.stringify(payload)],
    async () => {
      const { data } = await $api.post('/v1/auto-query-generation/generate', payload);

      dispatch(setTotalElements(data?.data?.totalElements as number | null));

      return { query: data?.data };
    },
    {
      enabled,
      onSuccess: ({ query }) => {
        dispatch(
          setCollectionAudienceItem({
            key: 'id',
            value: query?.id
          })
        );
      }
    }
  );

  return { ...data, ...arg };
}

export function useHouseCompact(index: number) {
  const dominant = useAppSelector(({ robotoCallReducer }) => robotoCallReducer.audience.dataType);
  const field = useAppSelector(({ robotoCallReducer }) => robotoCallReducer.audience.queries?.[index]?.field);

  const { data, ...arg } = useQuery<{ houses: HouseCompactType[] }>(
    ['collection-house-compact', dominant],
    async () => {
      const { data } = await $api.get(`/v1/house/compact/v2/${dominant}`);

      return { houses: data?.data };
    },
    {
      enabled: !!dominant && field === 'HOUSE'
    }
  );

  return { ...data, ...arg };
}

export function useSpeechKitEFields() {
  const audienceType = useAppSelector(({ robotoCallReducer }) => robotoCallReducer.audience.dataType);
  const endPoint =
    audienceType === 'DEBT' ? '/v1/sms-template/MONTH_PAYMENT_ONE_DAY_BEFORE/fields' : '/v1/lead/sms/fields';

  function convertToArray(obj: SpeechKitFieldsType): (TranslatedField & { name: string })[] {
    return Object.entries(obj).map(([name, value]) => ({
      ...value,
      name
    }));
  }

  const query = useQuery({
    queryKey: ['speech-kit-fields', endPoint],
    queryFn: async () => {
      const res = await $api.get(endPoint);

      return convertToArray(res?.data?.data);
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!endPoint,
    onError: baseErrorHandler
  });
  const { data, ...rest } = query;

  return {
    fields: data,
    ...rest
  };
}

export function useGetExampleAudio(audios: any[]) {
  const queryId = useAppSelector(({ robotoCallReducer }) => robotoCallReducer?.audience?.id);

  return useQuery({
    queryKey: ['example-audio', { queryId, audios }], // caching uchun
    queryFn: async () => {
      const res = await $api.post(
        `/v1/robot-call/calls/hear/audio`,
        { queryId: queryId ?? null, audios },
        { responseType: 'blob' }
      );

      return res.data;
    },
    enabled: false
  });
}
